import { cn } from "@/lib/utils";
import {
  accentClasses,
  type IndustryAccent,
} from "../industryTheme/industryTheme.util";
import MiniUiPreview from "../miniUiPreview/miniUiPreview.component";
import type { MiniUi } from "../miniUiPreview/miniUiPreview.type";

export interface MechanismCard {
  title: string;
  // Optional: a card can be just a preview and a title.
  text?: string;
  ui: MiniUi;
}

interface MechanismCardsProps {
  cards: MechanismCard[];
  accent?: IndustryAccent;
}

// Preview pane backgrounds, cycled by position. The first takes the page's
// accent; the rest step through the tone pairs (the preview's jt1..jt5).
const paneTones = [
  "bg-tone-sky",
  "bg-tone-mint",
  "bg-tone-lilac",
  "bg-tone-sun",
  "bg-tone-peach",
];

// "What Fydo can do for a <shop>" - the preview's `.jt-grid`. Each card has
// a soft preview pane with a mini UI widget (cashback, ticket, bubble...)
// above a numbered title and description. Cards lift on hover.
const MechanismCards = ({
  cards,
  accent = "brand",
}: MechanismCardsProps) => {
  const tone = accentClasses[accent];
  const paneTone = (index: number) =>
    index % 6 === 0 ? tone.soft : paneTones[(index % 6) - 1];

  return (
    // Always three to a row on desktop (owner's call), so every industry
    // page lays these out the same way.
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {cards.map((card, index) => (
        <article
          key={card.title}
          className="group relative flex flex-col overflow-hidden rounded-[30px] bg-white shadow-card transition-[translate,box-shadow] duration-300 ease-out hover:-translate-y-2 hover:shadow-lift motion-reduce:transition-none motion-reduce:hover:translate-y-0"
        >
          <div
            className={cn(
              "relative grid min-h-[190px] place-items-center overflow-hidden p-[26px]",
              paneTone(index),
            )}
          >
            <span
              aria-hidden="true"
              className="absolute -top-[90px] -right-[70px] size-60 rounded-full bg-white/55"
            />
            <span
              aria-hidden="true"
              className="absolute -bottom-[50px] -left-10 size-[120px] rounded-full bg-white/40"
            />
            <MiniUiPreview ui={card.ui} accent={accent} />
          </div>
          <div className="grid content-start gap-2 px-[26px] pt-[22px] pb-7">
            <span
              className={cn(
                "text-[12px] font-extrabold tracking-[0.1em]",
                tone.text,
              )}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3 className="text-[1.2rem] leading-snug font-bold tracking-[-0.01em] text-ink">
              {card.title}
            </h3>
            {card.text && (
              <p className="text-[15.5px] leading-relaxed text-ink-muted">
                {card.text}
              </p>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

export default MechanismCards;
